import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Label, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import TableDraggableSortableList from 'components/molecules/table-draggable-items/sortable-list/TableDraggableSortableList';
import { TopBar } from 'components/organisms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { getArtistsWithRolePlacement } from 'middleware/track/artistsWithRolePlacement';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiMicrophoneStage,
  PiNavigationArrow,
  PiTrash,
  PiUserCircleDashed,
} from 'react-icons/pi';
import { useGetArtistLightList } from 'services/artists/light';
import { usePostProductReplaceDuplicateArtists } from 'services/music-product-digital';
import { RELEASE_TYPE } from 'services/music-product-physical/types/enums';
import { IMusicProductPhysicalForm } from 'services/music-product-physical/types/form';
import { useGetTrackArtists } from 'services/tracks/artists';
import { ARTIST_ROLE } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const MusicProductPhysicalArtists = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<IMusicProductPhysicalForm> =
    useFormikContext<IMusicProductPhysicalForm>();
  const { getSelectOptions } = useEnumToSelectOptions();
  const { t } = useTranslation();

  const artistsLight = useGetArtistLightList();

  const [importArtists, setImportArtists] = useState<boolean>(false);

  const singleTrackArtists = useGetTrackArtists(
    values.tracks[0] && values.tracks[0].tracks[0]
      ? values.tracks[0].tracks[0].id
      : '',
    importArtists
  );

  useEffect(() => {
    if (
      !status?.displayMode &&
      !singleTrackArtists.isLoading &&
      singleTrackArtists.data &&
      importArtists &&
      values.releaseType === RELEASE_TYPE.single
    ) {
      setFieldValue(
        'artists',
        singleTrackArtists.data.map(a => ({
          id: a.id,
          role: a.role,
          nickname: a.nickname,
        })) ?? []
      );
      setImportArtists(false);
    }
  }, [
    singleTrackArtists.isLoading,
    singleTrackArtists.isRefetching,
    singleTrackArtists.data,
  ]);

  useEffect(() => {
    if (importArtists) {
      singleTrackArtists.refetch();
    }
  }, [importArtists]);

  const nodes = values.artists.map((artist, index) => ({
    ...artist,
    id: index + 1,
    index: index,
    artistId: artist.id,
    isDuplicated: artist.isDuplicated,
  }));

  const artistsSelectOptions =
    artistsLight.data?.map(artist => ({
      value: artist.id,
      label: artist.nickname,
    })) ?? [];

  const getFilteredRoleSelectOptionByArtist = (id: string) => {
    const selectOptions = getSelectOptions(ARTIST_ROLE, 'enum.ARTIST_ROLE');

    const artistSelectedRoles = values.artists
      .filter(artist => artist.id === id)
      .map(artist => artist.role);

    return selectOptions.filter(
      ({ value }) => !artistSelectedRoles.includes(value)
    );
  };

  const goToArtist = (id: string) => {
    window.open(`/artist/${id}/external`, '_blank');
  };

  const onAddArtist = () => {
    setFieldValue(`artists[${values.artists.length}]`, {
      id: '',
      nickname: '',
      role: ARTIST_ROLE.unknown,
    });
  };

  const onRemoveArtist = (index: number) => {
    setFieldValue(
      'artists',
      values.artists.filter((_, key) => key !== index)
    );
  };

  const isLatestArtistSelected = !!values.artists.length
    ? values.artists[values.artists.length - 1]?.role !== ARTIST_ROLE.unknown
    : true;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const movedArray = arrayMove(
        values.artists,
        parseInt(`${active.id}`) - 1,
        parseInt(`${over.id}`) - 1
      );

      setFieldValue('artists', movedArray);
    }
    document.body.style.setProperty('cursor', '');
  };
  const replaceDuplicates = usePostProductReplaceDuplicateArtists();

  const onReplaceDuplicates = async () =>
    await replaceDuplicates.mutateAsync(
      getArtistsWithRolePlacement(values.artists),
      {
        onSuccess: data =>
          setFieldValue(
            'artists',
            data.map(artist => ({
              id: artist.artistId,
              role: artist.artistRole,
              nickname: artist.nickname ?? '',
            }))
          ),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const shouldShowReplaceDuplicates = values.hasDuplicatedArtist;
  const shouldShowImport = !!(
    values.releaseType === RELEASE_TYPE.single &&
    values.tracks[0] && !!values.tracks[0].tracks.length
  );
  const shouldShowTopBar = shouldShowReplaceDuplicates || shouldShowImport;

  return (
    <>
      {!status?.displayMode && shouldShowTopBar && (
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <>
              {shouldShowReplaceDuplicates && (
                <Button
                  disabled={
                    !values.artists.length ||
                    singleTrackArtists.isLoading ||
                    singleTrackArtists.isRefetching
                  }
                  onClick={onReplaceDuplicates}
                  isLoading={replaceDuplicates.isPending}>
                  <Button.Icon icon={<PiUserCircleDashed />} />
                  <Button.Text text={t('button.replace-duplicates')} />
                </Button>
              )}
              {shouldShowImport && (
                <Button
                  disabled={
                    !(values.tracks[0] && !!values.tracks[0].tracks.length) ||
                    replaceDuplicates.isPending
                  }
                  isLoading={
                    singleTrackArtists.isLoading ||
                    singleTrackArtists.isRefetching
                  }
                  onClick={() => setImportArtists(true)}>
                  <Button.Icon icon={<PiMicrophoneStage />} />
                  <Button.Text text={t('button.import')} />
                </Button>
              )}
            </>
          }
        />
      )}
      {status?.displayMode && values.hasDuplicatedArtist && (
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex items-center gap-2 text-red'>
              <PiUserCircleDashed className='text-lg' />
              <span className='text-base font-semibold'>
                {t('toast-message.duplicated-artist-error')}
              </span>
            </div>
          }
        />
      )}
      <TableDraggableSortableList
        displayMode={status?.displayMode}
        handleDragEnd={handleDragEnd}
        onAdd={onAddArtist}
        size='compact'
        data={{ nodes: nodes }}
        columns={[
          {
            label: t(`input.label.nickname`),
            renderCell: ({ nickname, id, index, artistId }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={`${nickname}`} />
                ) : (
                  <FormInput.Select
                    name={`artists[${index}].id`}
                    isAsync
                    placeholder={t('input.label.nickname')}
                    isSearchable
                    label={t(`input.label.artist`)}
                    onChange={value => {
                      setFieldValue(`artists[${index}].nickname`, value.label);
                    }}
                    value={{
                      label: nickname,
                      value: artistId,
                    }}
                    options={artistsSelectOptions}
                    isLoading={artistsLight.isLoading}
                  />
                )}
              </TableItem>
            ),
          },
          {
            label: t(`input.label.role`),
            renderCell: ({ role, index, artistId }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text
                    text={t(`enum.ARTIST_ROLE.${ARTIST_ROLE[role]}`)}
                  />
                ) : (
                  <FormInput.Select
                    name={`artists[${index}].role`}
                    label={t(`input.label.role`)}
                    value={{
                      label: t(`enum.ARTIST_ROLE.${ARTIST_ROLE[role]}`),
                      value: role,
                    }}
                    options={getFilteredRoleSelectOptionByArtist(artistId)}
                    isDisabled={!values.artists[index].id}
                  />
                )}
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ index, isDuplicated }) => (
              <TableItem>
                {isDuplicated && (
                  <Label color='red' size='compact'>
                    <PiUserCircleDashed />
                    {t('music-product-digital.artists.is-duplicated')}
                  </Label>
                )}
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ artistId, index }) => (
              <TableItem justifyPosition='end'>
                {status?.displayMode ? (
                  <Button
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => goToArtist(artistId)}>
                                        <Button.Description description={t('button.open')} />

                                        <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                ) : (
                  <Button
                    type='button'
                    variant={BUTTON_VARIANT.remove}
                    isClear
                    onlyIcon
                    onClick={() => onRemoveArtist(index)}>
                                        <Button.Description description={t('button.remove')} />

                                        <Button.Icon icon={<PiTrash />} />
                  </Button>
                )}
              </TableItem>
            ),
          },
        ]}
      />
    </>
  );
};

export default MusicProductPhysicalArtists;
